import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { apiUrl } from '../utils/constants.ts';

// Constants
const MAX_GENERATED_IMAGES = 12;
const LOGO_GENERATION_URL = apiUrl + '/generate-logo';
const BANNER_GENERATION_URL = apiUrl + '/generate-banner';

interface GenerateImageSectionProps {
  tokenName: string;
  tokenSymbol: string;
  tokenDescription: string;
  setLogoFile: (file: File) => void;
  setLogoPreview: (url: string) => void;
  setBannerFile: (file: File) => void;
  setBannerPreview: (url: string) => void;
}

const GenerateImageSection: React.FC<GenerateImageSectionProps> = ({
  tokenName,
  tokenSymbol,
  tokenDescription,
  setLogoFile,
  setLogoPreview,
  setBannerFile,
  setBannerPreview,
}) => {
  // State for generated images, current index, loading, and additional instructions
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isGeneratingLogo, setIsGeneratingLogo] = useState<boolean>(false);
  const [isGeneratingBanner, setIsGeneratingBanner] = useState<boolean>(false);
  const [additionalInstructions, setAdditionalInstructions] = useState<string>('');

  // Handle logo generation
  const generateLogo = async () => {
    if (generatedImages.length >= MAX_GENERATED_IMAGES) {
      toast.error(`Maximum limit of ${MAX_GENERATED_IMAGES} images reached`);
      return;
    }

    if (!tokenName || !tokenSymbol || !tokenDescription) {
      toast.error('Please fill in Token Name, Simbol Name, and Token Description');
      return;
    }

    setIsGeneratingLogo(true);
    try {
      const response = await fetch(LOGO_GENERATION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tokenName, tokenSymbol, tokenDescription, additionalInstructions }),
      });

      if (!response.ok) throw new Error('Failed to generate logo');
      const data = await response.json();
      const imageUrl = data.imageUrl;

      setGeneratedImages([...generatedImages, imageUrl]);
      setCurrentIndex(generatedImages.length); // Show the latest image
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate logo');
    } finally {
      setIsGeneratingLogo(false);
    }
  };

  // Handle banner generation
  const generateBanner = async () => {
    if (generatedImages.length >= MAX_GENERATED_IMAGES) {
      toast.error(`Maximum limit of ${MAX_GENERATED_IMAGES} images reached`);
      return;
    }

    if (!tokenName || !tokenSymbol || !tokenDescription) {
      toast.error('Please fill in Token Name, Simbol, and Token Description');
      return;
    }

    setIsGeneratingBanner(true);
    try {
      const response = await fetch(BANNER_GENERATION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tokenName, tokenSymbol, tokenDescription, additionalInstructions }),
      });

      if (!response.ok) throw new Error('Failed to generate banner');
      const data = await response.json();
      const imageUrl = data.imageUrl;

      setGeneratedImages([...generatedImages, imageUrl]);
      setCurrentIndex(generatedImages.length); // Show the latest image
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate banner');
    } finally {
      setIsGeneratingBanner(false);
    }
  };

  // Navigation handlers
  const goBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const goForward = () => {
    if (currentIndex < generatedImages.length - 1) setCurrentIndex(currentIndex + 1);
  };

  // Clear all generated images
  const clearImages = () => {
    setGeneratedImages([]);
    setCurrentIndex(0);
    toast.success('Generated images cleared');
  };

  // Set current image as logo
  const setAsLogo = async () => {
    if (generatedImages.length === 0) return;
    const imageUrl = apiUrl + generatedImages[currentIndex];
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'generated_logo.png', { type: 'image/png' });
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(blob));
      toast.success('Logo set successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to set logo');
    }
  };

  // Set current image as banner
  const setAsBanner = async () => {
    if (generatedImages.length === 0) return;
    const imageUrl = apiUrl + generatedImages[currentIndex];
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], 'generated_banner.png', { type: 'image/png' });
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(blob));
      toast.success('Banner set successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to set banner');
    }
  };

  // Download the current image
  const downloadImage = async () => {
    if (generatedImages.length === 0) {
      toast.error('No image to download');
      return;
    }
    const imageUrl = apiUrl + generatedImages[currentIndex];
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `generated_image_${currentIndex + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url); // Clean up the URL object
      toast.success('Image downloaded');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download image');
    }
  };

  return (
    <section className="flex flex-col w-full">
      <p className="text-[12px] text-[#7db4e5] mb-2 order-1">Generate Image</p>
      <div className="order-2 bg-[#050f1a] px-[16px] py-[14px] rounded-[10px] flex flex-col items-center w-full">
        {/* Preview Area with Clear Button */}
        <div className="relative flex justify-center w-full mb-4">
          {generatedImages.length > 0 ? (
            <img
              src={apiUrl + generatedImages[currentIndex]}
              alt="Generated Preview"
              className="object-contain w-64 h-64 rounded"
            />
          ) : (
            <div className="flex items-center justify-center w-64 h-64 bg-gray-700 rounded">
              <p className="text-gray-400">No image generated</p>
            </div>
          )}
          {generatedImages.length > 0 && (
            <button
              onClick={clearImages}
              className="absolute top-0 right-0 px-3 py-1 text-white bg-red-600 rounded rounded-tl-none rounded-br-none hover:bg-red-700"
            >
              Clear
            </button>
          )}
        </div>

        {/* Navigation and Counter */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={goBack}
            disabled={currentIndex === 0 || generatedImages.length === 0}
            className="px-2 py-1 text-white bg-gray-600 rounded disabled:opacity-50"
          >
            &lt;
          </button>
          <span className="text-white">
            {generatedImages.length > 0
              ? `${currentIndex + 1}/${generatedImages.length}`
              : '0/0'}
          </span>
          <button
            onClick={goForward}
            disabled={currentIndex === generatedImages.length - 1 || generatedImages.length === 0}
            className="px-2 py-1 text-white bg-gray-600 rounded disabled:opacity-50"
          >
            &gt;
          </button>
        </div>

        {/* Generate Logo and Generate Banner Buttons */}
        <div className="flex w-full gap-4 mb-4">
          <button
            onClick={generateLogo}
            disabled={isGeneratingLogo || isGeneratingBanner || generatedImages.length >= MAX_GENERATED_IMAGES}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-[#1c5290] to-[#45c0be] text-white rounded disabled:opacity-50"
          >
            {isGeneratingLogo ? 'Generating...' : 'Generate Logo'}
          </button>
          <button
            onClick={generateBanner}
            disabled={isGeneratingLogo || isGeneratingBanner || generatedImages.length >= MAX_GENERATED_IMAGES}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-[#1c5290] to-[#45c0be] text-white rounded disabled:opacity-50"
          >
            {isGeneratingBanner ? 'Generating...' : 'Generate Banner'}
          </button>
        </div>

        {/* Set as Logo/Banner Buttons */}
        <div className="flex w-full gap-4 mb-4">
          <button
            onClick={setAsLogo}
            disabled={generatedImages.length === 0}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-[#1c5290] to-[#45c0be] text-white rounded disabled:opacity-50"
          >
            Set as Logo
          </button>
          <button
            onClick={setAsBanner}
            disabled={generatedImages.length === 0}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-[#1c5290] to-[#45c0be] text-white rounded disabled:opacity-50"
          >
            Set as Banner
          </button>
        </div>

        {/* Download Image Button */}
        <div className="w-full mb-4">
          <button
            onClick={downloadImage}
            disabled={generatedImages.length === 0}
            className="w-full px-4 py-2 bg-gradient-to-r from-[#1c5290] to-[#45c0be] text-white rounded disabled:opacity-50"
          >
            Download Image
          </button>
        </div>

        {/* Additional Instructions Text Area */}
        <div className="w-full">
          <p className="text-[12px] text-[#7db4e5] mb-2">Additional Instructions</p>
          <textarea
            placeholder="(Optional, may include character or logo description, or specific details like 'Simbol name should be a green neon sign')"
            value={additionalInstructions}
            onChange={(e) => setAdditionalInstructions(e.target.value)}
            rows={3}
            className="w-full px-4 py-2 bg-[#1a2a3a] text-white rounded border border-gray-600 focus:outline-none focus:border-blue-500 resize-y"
          />
        </div>
      </div>
    </section>
  );
};

export default GenerateImageSection;